import Minze from 'minze'
import { normalizeDateToUnix } from '../../modules/date'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { IFacet, IFacetOption, ISearchConfig } from '../../modules/search'
import { convertTimeRangeToFilterFacet, getTimeRangeFromTime, Time } from '../../modules/time'
import { configs } from './configs'
import './locales'
import { DataPackConfig, LvcActivityPackSubscriber } from './lvc-activity-pack'
import { LvcItemSourceData } from '../../../types/global'
import { EventStatus } from '../../componentsV2/lvc-card/lvc-event-status'

export enum EventAvailability {
  InStock = 'InStock',
  Available = 'Available',
  SoldOut = 'SoldOut',
  OutOfStock = 'OutOfStock',
  InStoreOnly = 'InStoreOnly',
}

export enum PrebuiltPacks {
  recommended = 'recommended',
  nearMe = 'nearMe',
  thisWeek = 'thisWeek',
  free = 'free',
  everyone = 'everyone',
  familyFriendly = 'familyFriendly',
  expositions = 'expositions',
  series = 'series',
}

const GEO_ENABLED_INDEXES: string[] = [
  'places_full',
  'events_search',
  'exhibitions_search',
  'eventseries_search',
  'festivals_search',
]
const GEO_ENABLED_INDEXES_TO_IGNORE: string[] = []

const DEFAULT_SEARCH_RADIUS = 30

const NOW = new Date()
const NOW_UNIX = Math.floor(NOW.getTime() / 1000)

const UNIX_OFFSET = 7 * 24 * 60 * 60 // 30 days in seconds

interface PackItem {
  title?: string
  items: any[]
  total: number
  disableViewMore?: boolean
  disableLimit?: boolean
  searchConfig?: ISearchConfig
}

export const fetchPackItems = async (
  subscriber: LvcActivityPackSubscriber,
  dataPackConfig?: DataPackConfig,
  packTag?: PrebuiltPacks,
  location?: any
): Promise<PackItem | null> => {
  subscriber?._core?.resetAllConfigs() // Reset the search config

  if (dataPackConfig) {
    const result = await fetchPackItemsByConfig(subscriber, dataPackConfig, location)
    if (result) {
      return result
    }
    console.warn('fetchPackItemsByConfig returned null, returning empty pack items')
    return {
      items: [],
      total: 0,
      searchConfig: {},
      disableViewMore: false,
      disableLimit: false,
    }
  }
  if (packTag) {
    const result = await fetchPrebuiltPackItems(subscriber, packTag, location)
    if (result) {
      return result
    }
    console.warn('fetchPrebuiltPackItems returned null, returning empty pack items')
    return {
      items: [],
      total: 0,
      searchConfig: {},
      disableViewMore: false,
      disableLimit: false,
    }
  }
  return null
}

export const fetchPackItemsByConfig = async (
  subscriber: LvcActivityPackSubscriber,
  dataConfig: DataPackConfig,
  location?: any
) => {
  // Fetch the pack items based on the dataConfig
  // compile facets and execute search
  try {
    const { type, limit, sortBy, flexDate, startDate, endDate, city, radius, clientId, eventList } =
      dataConfig

    // If not manually selected list, compile the facets from CMS
    if (!eventList || eventList.length === 0) {
      const index = compileIndex(type) + compileSortAffix(sortBy)
      let searchConfig = {
        index: [index] || '',
        modal: compileIndex(type),
        type,
        limit,
        sortBy,
        facets: compileFacetConfig(dataConfig),
        dateFilters: applyDateFilters(startDate, endDate, flexDate?.[0]),
        hitsPerPage: {
          [index]: limit || 30,
        },
        customPartnerId: clientId?.toString(),
      } as ISearchConfig

      if (index && index.trim() !== '') {
        // If geo enabled index, and radius is set
        if (isGeoEnabledIndex(index) && radius !== 999999) {
          // If city is provided, get the lat long of the city then use it as aroundLatLng
          if (city && city.length > 0) {
            const cityValue = city[0] as string
            const cityResponse = await MainModule.getInstance().getGeo().list(cityValue)
            if (cityResponse && cityResponse.length > 0) {
              const { latitude, longitude } = cityResponse[0]
              if (latitude && longitude) {
                searchConfig.options = {
                  ...searchConfig.options,
                  aroundLatLng: {
                    [index]: `${latitude},${longitude}`,
                  },
                  aroundRadius: {
                    [index]: radius ? radius * 1000 : DEFAULT_SEARCH_RADIUS * 1000, // in meters
                  },
                }
              }
            }
          } else {
            // If city is not provided, use the current user location
            if (location) {
              searchConfig.options = {
                ...searchConfig.options,
                aroundLatLng: {
                  [index]: `${location.latitude},${location.longitude}`,
                },
                aroundRadius: {
                  [index]: radius ? radius * 1000 : DEFAULT_SEARCH_RADIUS * 1000, // in meters
                },
              }
            }
          }
        }
      }
      const results = await subscriber?._core?.executeSearch(searchConfig)

      if (results && index && results[index]) {
        return {
          items: removeDuplicatesAndFilterCancelledSoldOut(results[index].items || []),
          total: results[index].total || 0,
          searchConfig: subscriber?._core?.searchConfig,
        }
      } else {
        console.warn('Search results or index is undefined, returning empty results')
        return {
          items: [],
          total: 0,
          searchConfig: subscriber?._core?.searchConfig,
        }
      }
    } else {
      // Convert manual event list {objectID, index}
      const compiledList = compileItemList(eventList)
      const indexMap = [
        'events',
        'events_search',
        'eventseries_search',
        'exhibitions_search',
        'places_full',
        'artists',
      ]
      const finalList: any[] = []
      for (const index of indexMap) {
        let bulkList = compiledList
          .filter(item => item.index === index)
          .map(item => {
            return { indexNumber: item.indexNumber, objectID: item.objectID }
          })
        if (index === 'events') {
          const results = []
          for (const item of bulkList) {
            const eventOfferResult = await fetchOfferForEvent(item.objectID, subscriber)
            results.push({ ...item, result: eventOfferResult })
          }
          finalList.push(...results?.filter(Boolean))
        } else {
          let bulkResults = await subscriber?._core?.getObjects(
            index,
            bulkList.map(item => item.objectID)
          )
          const interList: any[] = []
          for (let item of bulkList) {
            bulkResults?.filter(Boolean).map(result => {
              const found = item.objectID === result.objectID
              if (found) {
                interList.push({
                  indexNumber: item.indexNumber,
                  result: result,
                })
              }
            })
          }
          finalList.push(...interList)
        }
      }
      const sortedList = finalList.sort((a, b) => a.indexNumber - b.indexNumber)
      // Remove items that are past current time.
      const finalResultsList = sortedList
        .map(item => item.result)
        .filter(Boolean)
        .filter(item => {
          const startDateUnix = item.startDate
            ? normalizeDateToUnix(item.startDate)?.valueOf()
            : null
          const endDateUnix = item.endDate ? normalizeDateToUnix(item.endDate)?.valueOf() : null

          // We only care about startDate for eventOffer type content
          if (item.table === 'eventOffers' && startDateUnix && startDateUnix < NOW.getTime()) {
            return false
          }
          // Rest of content we filter out if endDate and endDate has past
          if (endDateUnix && endDateUnix < NOW.getTime()) {
            return false
          }
          // All other cases
          return true
        })
      const cleanedResults = removeDuplicatesAndFilterCancelledSoldOut(finalResultsList || [])
      return {
        items: cleanedResults || [],
        total: cleanedResults.length || 0,
        disableViewMore: true,
        disableLimit: true,
      }
    }
  } catch (err) {
    console.error(err, 'Error fetching pack items by config')
    return null
  }
}
const fetchOfferForEvent = async (item: any, subscriber: LvcActivityPackSubscriber) => {
  const baseSearchConfig = subscriber?._core?.searchConfig || {}
  const searchConfig = {
    ...baseSearchConfig,
    index: ['events_search'],
    facets: [
      {
        name: 'eventId',
        options: [
          {
            available: true,
            selected: true,
            value: item,
          },
        ],
      },
    ],
  }
  const result = await subscriber?._core?.executeSearch(searchConfig)
  if (result) {
    const items = result?.['events_search']?.items
    if (items && items.length > 0) {
      return items[0]
    }
  }
}
export const fetchPrebuiltPackItems = async (
  subscriber: LvcActivityPackSubscriber,
  packTag: PrebuiltPacks,
  location?: any
) => {
  // Fetch items from prebuilt pack subscriber functions
  // Use searchConfig from Configs.
  try {
    await subscriber?._core?.resetAllConfigs() // Reset the search config
    let searchConfig = {} as ISearchConfig
    searchConfig = { ...configs.searchConfigs[packTag] }
    if (packTag === PrebuiltPacks.recommended) {
      searchConfig.facets = [await compileUserDisciplineFacets(), ...(searchConfig?.facets || [])]
    }

    const index = searchConfig?.index?.[0]
    if (index) {
      if (isGeoEnabledIndex(index) && location) {
        searchConfig.options = {
          ...searchConfig.options,
          aroundLatLng: {
            [index]: `${location.latitude},${location.longitude}`,
          },
          aroundRadius: {
            [index]: DEFAULT_SEARCH_RADIUS * 1000, // in meters
          },
        }
      }

      const results = await subscriber?._core?.executeSearch(searchConfig)
      if (packTag === PrebuiltPacks.recommended) {
        Minze.cast('lvc:recommendedPack_results', {
          total: results?.[index]?.total || 0,
        })
      }
      return {
        title: Locale.getKey(`prebuiltActivityPack.${packTag}`),
        items: removeDuplicatesAndFilterCancelledSoldOut(results?.[index]?.items || []),
        total: results?.[index]?.total || 0,
        searchConfig: subscriber?._core?.searchConfig,
      }
    } else {
      return null
    }
  } catch (err) {
    console.error(err, 'Error fetching prebuilt pack items')
    return null
  }
}
export const compileUserDisciplineFacets = async () => {
  const userDisciplines: { [key: string]: string[] } =
    (await MainModule.getInstance().getAuth().getUserData()?.profile?.culturalProfile) ?? {}
  const facetValues: string[] = []
  for (let value of Object.values(userDisciplines)) {
    if (value.length > 0) {
      value.map(item => facetValues.push(item))
    }
  }
  return {
    name: 'uiAdditionalTypesCode',
    options: facetValues.map(key => {
      return {
        value: key,
        selected: true,
        available: true,
      } as IFacetOption
    }) as IFacetOption[],
  } as IFacet
}
export const compileItemList = (itemList: { url: string }[]) => {
  // TODO: refactor to create list to group indexs so that we can bulk query, instead of each object.
  // We will need to add an indexNumber so that we can revert back to original array map, after bulk fetch.
  return itemList.map((item, indexNumber) => {
    const url = item.url
    const urlSplit = url.split('/')
    const contentType = urlSplit.find(url =>
      ['evenement', 'exposition', 'series', 'lieu', 'artiste', 'organisation'].includes(url)
    )
    let objectID = ''
    let index = ''
    switch (contentType) {
      case 'lieu':
        index = 'places_full'
        objectID = `places-${urlSplit[urlSplit.length - 1]}`
        break
      case 'artiste':
      case 'organisation':
        index = 'artists'
        objectID = `contributors-${urlSplit[urlSplit.length - 1]}`
        break
      case 'series':
        index = 'eventseries_search'
        objectID = `eventSeries-${urlSplit[urlSplit.length - 1]}`
        break
      case 'exposition':
        index = 'exhibitions_search'
        objectID = `exhibitionEvents-${urlSplit[urlSplit.length - 1]}`
        break
      case 'evenement':
        if (urlSplit.length < 8) {
          index = 'events'
          objectID = `${urlSplit[urlSplit.length - 1]}` // ex: 9999
          break
        } else {
          index = 'events_search'
          objectID = `eventOffers-${urlSplit[urlSplit.length - 1]}`
          break
        }
      default:
        index = 'events_search'
        objectID = `eventOffers-${urlSplit[urlSplit.length - 1]}`
    }
    return { indexNumber, index, objectID }
  })
}
export const compileIndex = (type: string) => {
  switch (type) {
    case 'offers':
      return 'events_search'
    case 'events':
      return 'events'
    case 'exhibitions':
      return 'exhibitions_search'
    case 'series':
      return 'eventseries_only'
    case 'festivals':
      return 'festivals_search'
    case 'places':
      return 'places_full'
    case 'contributors':
      return 'artists'
    default:
      return 'events_search'
  }
}
export const compileFacetConfig = (data: DataPackConfig) => {
  // Map the parsedData from Squidex to the IFacet[] format
  let finalFacetList = [] as IFacet[]
  const { type, limit, audience, discipline, isAccessibleForFree, flexDate } = data
  if (audience && audience.length > 0 && Array.isArray(audience)) {
    let audienceFacet: IFacet = {
      name: type === 'events' ? 'audienceArray' : 'audience',
      options: [],
    }
    audience.map(aud => {
      audienceFacet.options.push({
        available: true,
        selected: true,
        value: aud,
      })
    })
    finalFacetList.push(audienceFacet)
  }
  if (discipline && discipline.length > 0 && Array.isArray(discipline)) {
    let disciplineFacet: IFacet = {
      name: 'uiAdditionalTypesCode',
      options: [],
    }
    discipline.map(disc => {
      disciplineFacet.options.push({
        available: true,
        selected: true,
        value: disc,
      })
    })
    finalFacetList.push(disciplineFacet)
  }
  if (isAccessibleForFree) {
    let freeFacet: IFacet = {
      name: 'isAccessibleForFree',
      options: [
        {
          available: true,
          selected: true,
          value: isAccessibleForFree.toString(),
        },
      ],
    }
    finalFacetList.push(freeFacet)
  }
  return finalFacetList
}

export const compileSortAffix = (sortBy: string | undefined | null) => {
  switch (sortBy) {
    case 'startDate_asc':
      return '_startDate_asc'
    case 'endDate_asc':
      return '_endDate_asc'
    default:
      return ''
  }
}

export const isGeoEnabledIndex = (index: string) => {
  // Check if the index starts with any base index in GEO_ENABLED_INDEXES
  // and ensure it's not in GEO_ENABLED_INDEXES_TO_IGNORE
  return (
    GEO_ENABLED_INDEXES.some(baseIndex => index.startsWith(baseIndex)) &&
    !GEO_ENABLED_INDEXES_TO_IGNORE.includes(index)
  )
}

export const applyDateFilters = (startDate: string, endDate: string, flexDate: string) => {
  if (!flexDate) {
    return [
      new Date(startDate)?.getTime() / 1000 || NOW_UNIX,
      new Date(endDate)?.getTime() / 1000 || NOW_UNIX + UNIX_OFFSET,
    ]
  } else {
    return flexDateToDateFilter(flexDate)
  }
}
export const flexDateToDateFilter = (flexDate: string) => {
  if (!flexDate) return [NOW_UNIX, NOW_UNIX + UNIX_OFFSET]
  switch (flexDate) {
    case 'this week':
      return convertTimeRangeToFilterFacet(getTimeRangeFromTime(Time.THIS_WEEK))
    case 'this weekend':
      return convertTimeRangeToFilterFacet(getTimeRangeFromTime(Time.THIS_WEEKEND))
    case 'next week':
      return convertTimeRangeToFilterFacet(getTimeRangeFromTime(Time.NEXT_WEEK))
    case 'next weekend':
      return convertTimeRangeToFilterFacet(getTimeRangeFromTime(Time.NEXT_WEEKEND))
    case 'this month':
    case 'next 30 days':
      return convertTimeRangeToFilterFacet(getTimeRangeFromTime(Time.NEXT_30_DAYS))
    case 'all':
      return []
    default:
      return [NOW_UNIX, NOW_UNIX + UNIX_OFFSET]
  }
}

// Remove duplicate events from results list, by 'eventId'. Only allow the first unique result per list.
// return the list with the duplicate result removed.
export const removeDuplicateEvents = (results: LvcItemSourceData[]) => {
  if (!results || results.length === 0) {
    return []
  }

  const seenEventIds = new Set<string>()
  const uniqueResults: LvcItemSourceData[] = []

  for (const result of results) {
    if (result.eventId && !seenEventIds.has(result.eventId)) {
      seenEventIds.add(result.eventId)
      uniqueResults.push(result)
    } else if (!result.eventId) {
      // If an item doesn't have an eventId, we'll include it anyway
      uniqueResults.push(result)
    }
  }

  return uniqueResults
}

export const removeCancelledAndSoldOutEvents = (results: LvcItemSourceData[]) => {
  // Filter out results where status is Cancelled or Availability is SoldOut or OutOfStock
  return results.filter(result => {
    return (
      result.status !== EventStatus.Cancelled &&
      result.availability !== EventAvailability.SoldOut &&
      result.availability !== EventAvailability.OutOfStock
    )
  })
}

export const removeDuplicatesAndFilterCancelledSoldOut = (results: LvcItemSourceData[]) => {
  return removeCancelledAndSoldOutEvents(removeDuplicateEvents(results))
}
